import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLazyQuery, useMutation } from "@apollo/client";
import { ISignupVars, SIGNUP } from "graphql/mutations/signup";
import { ILoginVars, LOGIN } from "graphql/mutations/login";
import { GET_AUTH_STATUS, IGetAuthStatusVars } from "graphql/queries/getAuthStatus";
import { LOGIN_VIA_OTP, ILoginOtpVars } from "graphql/mutations/loginViaOtp";
import { VERIFY_OTP, IVerifyOtpVars } from "graphql/mutations/verifyOtp";
import { RESET_PASSWORD, IResetPasswordVars } from "graphql/mutations/resetPassword";
import { validateEmail } from "../utils";
import { brandName } from "constants/index";
import { persistAuth } from "utils/auth";

export interface AuthFlowOptions {
  /** Prefill + lock the email (connect flow). */
  lockedEmail?: string;
  /** Auto-run the email-first step once the email is known (connect flow). */
  autoStart?: boolean;
  /**
   * Fires at every successful persistAuth point (login / OTP / reset).
   * Login page: navigate to dashboard. Connect: attach profile + redirect.
   * Awaited so callers can gate their next step on it.
   */
  onAuthenticated: (role: string | null) => void | Promise<void>;
}

// The email → password / signup / OTP / reset-password state machine shared by
// the login page and the connect page. Handlers are moved verbatim from the
// original sign-in component; the only change is that the three auth-success
// points call `onAuthenticated(role)` instead of navigating directly.
export function useAuthFlow({ lockedEmail, autoStart, onAuthenticated }: AuthFlowOptions) {
  const [email, setEmail] = useState<string>(lockedEmail || "");
  const [fullName, setFullName] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSignup, setIsSignUp] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [showOtp, setShowOtp] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [forgotPsw, setForgotPsw] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [signupMutation] = useMutation<ISignupVars>(SIGNUP);
  const [loginMutation] = useMutation<ILoginVars>(LOGIN);
  const [verifyOtp] = useMutation<IVerifyOtpVars>(VERIFY_OTP);
  const [resetPassword] = useMutation<IResetPasswordVars>(RESET_PASSWORD);
  const [getAuthStatus] = useLazyQuery<IGetAuthStatusVars>(GET_AUTH_STATUS);
  const [loginViaOtp] = useMutation<ILoginOtpVars>(LOGIN_VIA_OTP);

  const emailLocked = !!lockedEmail;

  // Keep the email in sync if the locked value arrives asynchronously.
  useEffect(() => {
    if (lockedEmail) setEmail(lockedEmail);
  }, [lockedEmail]);

  const handleEmailSubmit = async () => {
    setLoading(true);
    if (email) {
      const isEmailValid = validateEmail(email);
      if (isEmailValid) {
        setEmailError("");
        getAuthStatus({ variables: { email } })
          .then(async (res: any) => {
            const status = res.data.getAuthStatus.status;
            if (status === "NOT_FOUND") {
              setIsSignUp(true);
            } else if (status === "EMAIL_NOT_VERIFIED") {
              toast.error("Email not verified, please verified it.");
              await handleForgotPassword(true);
            } else {
              setShowPassword(true);
            }
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
            console.error("There was an error fetching email data:", err);
          });
      } else {
        setEmailError("Invalid Email Address");
        setLoading(false);
      }
    } else {
      setEmailError("Invalid Email Address");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (resetPsw = false) => {
    setLoading(true);
    loginViaOtp({ variables: { email } })
      .then((res: any) => {
        setShowOtp(res.data.loginViaOtp.token);
        setIsSignUp(false);
        if (resetPsw) setForgotPsw(res.data.loginViaOtp.token);
        else setForgotPsw("");
        setShowPassword(false);
        setLoading(false);
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(e?.message);
      });
  };

  const handleLogin = async () => {
    if (password) {
      try {
        setLoading(true);
        loginMutation({ variables: { password, email } })
          .then(async (res: any) => {
            if (res.data) {
              const roleVal = persistAuth(res.data.login.token) ?? res?.data?.login?.type;
              toast.success("Login Successful!!");
              await onAuthenticated(roleVal);
            }
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
            toast.error(err.message || "An error occurred during login");
          });
      } catch (error: any) {
        setLoading(false);
        toast.error(error);
      }
    }
  };

  const handleSignup = async () => {
    if (fullName && password) {
      setLoading(true);
      signupMutation({ variables: { email, fullName, password } })
        .then((res: any) => {
          if (res?.data?.signup?.token) {
            setShowOtp(res?.data?.signup?.token);
            setIsSignUp(false);
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          toast.error(err?.response?.data?.message || "Error fetching email data");
        });
    } else {
      toast.error(`Please enter the ${!fullName ? "name" : "password"}`);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      toast.error("Invalid Otp");
      return;
    }
    setLoading(true);
    verifyOtp({ variables: { params: { token: showOtp, otp } } })
      .then(async (res: any) => {
        if (!forgotPsw) {
          const roleVal = persistAuth(res.data.verifyOtp.token);
          toast.success("Login Successful!!");
          await onAuthenticated(roleVal);
        } else {
          setForgotPsw(res.data.verifyOtp.token);
          setShowOtp("");
        }
        setLoading(false);
      })
      .catch((err: any) => {
        setLoading(false);
        setOtp("");
        toast.error(err?.message || "Invalid OTP");
      });
  };

  const handleResetPassword = async () => {
    setLoading(true);
    resetPassword({
      variables: { password },
      context: { headers: { Authorization: `Bearer ${forgotPsw}` } },
    })
      .then(async () => {
        const roleVal = persistAuth(forgotPsw);
        toast.success("Password reset succesfully");
        await onAuthenticated(roleVal);
        setLoading(false);
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(e?.message);
      });
  };

  const handleSubmit = () => {
    if (isSignup) handleSignup();
    else if (showPassword) handleLogin();
    else if (showOtp) handleVerifyOtp();
    else if (!showOtp && forgotPsw) handleResetPassword();
    else handleEmailSubmit();
  };

  const onKeyDownHandler = (e: any) => {
    if (e?.keyCode === 13) {
      e?.target?.blur();
      e?.preventDefault();
      handleSubmit();
    }
  };

  // Auto-run the email step once, for the connect flow where email is pre-known.
  const startedRef = useRef(false);
  useEffect(() => {
    if (autoStart && email && !startedRef.current) {
      startedRef.current = true;
      handleEmailSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, email]);

  const title = showOtp
    ? "Confirmation Awaits!"
    : forgotPsw
    ? "Secure Access"
    : `Welcome to ${brandName}`;

  const subtitle = showOtp
    ? "We've sent a One-Time Password(OTP) to your email. Please enter it below to verify your account."
    : isSignup
    ? "Lets get started!"
    : forgotPsw
    ? "Now! Please enter your new password to unlock your Account."
    : "Let's get you connected. Please enter your work email to begin.";

  const submitLabel = showOtp || showPassword || forgotPsw ? "Submit" : " Next";

  return {
    // state
    email,
    fullName,
    password,
    otp,
    emailError,
    isSignup,
    showPassword,
    showOtp,
    forgotPsw,
    loading,
    emailLocked,
    // setters
    setEmail,
    setFullName,
    setPassword,
    setOtp,
    setEmailError,
    // handlers
    handleSubmit,
    handleForgotPassword,
    onKeyDownHandler,
    // derived copy
    title,
    subtitle,
    submitLabel,
  };
}

export type AuthFlow = ReturnType<typeof useAuthFlow>;
